/**
 * Поведения пяти героев (E3) для ядра боя: обычная атака со своим выбором цели, `projectileCount`, навык по
 * `SkillDef.kind` с силой и перезарядкой от звёзд и модификаторов, статусы зомби.
 *
 * Поведения не хранят состояние боя в замыканиях (один объект на все бои): изменяемое — в `HeroEntity.data` и
 * `ZombieEntity.data` (в хэше), производное от входа боя (характеристики навыка) — в `WeakMap` по сущности героя.
 */
import type { HeroId, SkillDef } from "@newfungame/contracts";
import type { HeroBehavior, HeroEntity, SimApi, ZombieEntity } from "../sim/types";
import { projectileCount, skillStats } from "./skill-stats";
import { SKILL_HANDLERS, type SkillContext, type SkillHandler } from "./skills";
import { refreshAllStatuses, slowZombie } from "./status";
import { densestCrowd, extraTargets, fastest, nearestToHq, unslowedFirst, type Targeting } from "./targeting";

/** Обычное попадание Frosty замедляет на 30 % на 1,5 с (в данных героя нет — константа E3). */
export const FROST_HIT_SLOW_PERMILLE = 300;
export const FROST_HIT_SLOW_TICKS = 45;

interface HeroCache {
  skill: SkillDef | null;
  handler: SkillHandler | null;
  ctx: SkillContext | null;
  shots: number;
}

const cache = new WeakMap<HeroEntity, HeroCache>();

function prepare(hero: HeroEntity, sim: SimApi): HeroCache {
  let c = cache.get(hero);
  if (c) return c;
  const skill = sim.balance.skills.find((s) => s.id === hero.skill.skillId) ?? null;
  const handler = skill ? SKILL_HANDLERS[skill.kind] : null;
  const stats = skill ? skillStats(sim.balance, skill, hero.heroId, hero.level, hero.stars, sim.modifiers) : null;
  c = {
    skill,
    handler,
    ctx: skill && stats ? { hero, sim, skill, stats } : null,
    shots: projectileCount(hero.heroId, sim.modifiers),
  };
  cache.set(hero, c);
  return c;
}

/** Обычная стрельба героя: как `shooterTick` ядра, плюс лишние снаряды и ускорение от навыка. */
function shoot(hero: HeroEntity, sim: SimApi, c: HeroCache, target: Targeting): void {
  if (hero.cooldownLeft > 0) hero.cooldownLeft--;
  if (hero.cooldownLeft > 0) return;
  const primary = target(hero, sim);
  if (!primary) {
    hero.targetId = null;
    return;
  }
  const over = c.ctx && c.handler?.shooting ? c.handler.shooting(c.ctx) : null;
  const extra = c.shots - 1 + (over?.extraShots ?? 0);
  if (extra > 0) for (const z of extraTargets(hero, sim, primary, extra)) sim.fire(hero, z);
  sim.fire(hero, primary); // последним: цель и взгляд героя — основная цель
  if (over) hero.cooldownLeft = over.cooldownTicks;
}

export interface HeroBehaviorOptions {
  target?: Targeting;
  onHit?: (hero: HeroEntity, target: ZombieEntity, sim: SimApi) => void;
}

/** Поведение героя: выбор цели, навык из данных, статусы. */
export function heroBehavior(opts: HeroBehaviorOptions = {}): HeroBehavior {
  const target = opts.target ?? nearestToHq;
  return {
    init(hero, sim) {
      const c = prepare(hero, sim);
      if (!c.ctx || !c.handler) return;
      hero.skill.cooldownTotal = c.ctx.stats.cooldownTicks;
      hero.skill.cooldownLeft = c.ctx.stats.cooldownTicks;
      hero.skill.durationTicks = c.handler.duration(c.ctx.skill, c.ctx.stats);
    },
    tick(hero, sim) {
      const c = prepare(hero, sim);
      refreshAllStatuses(sim);
      if (c.ctx && c.handler?.tick) c.handler.tick(c.ctx);
      shoot(hero, sim, c, target);
    },
    castSkill(hero, sim) {
      const c = prepare(hero, sim);
      return c.ctx && c.handler ? c.handler.cast(c.ctx) : false;
    },
    onHit: opts.onHit,
  };
}

/** Frosty: обычный лёд замедляет. */
export function frostHit(_hero: HeroEntity, z: ZombieEntity, sim: SimApi): void {
  slowZombie(z, sim.tick, FROST_HIT_SLOW_TICKS, FROST_HIT_SLOW_PERMILLE);
}

/** Поведения героев v1. */
export const HERO_BEHAVIORS: Record<HeroId, HeroBehavior> = {
  gunner: heroBehavior(), // одиночная цель — ближайший к штабу
  bomber: heroBehavior({ target: densestCrowd }), // бомба — в самую густую толпу
  sparky: heroBehavior({ target: fastest }), // мгновенная искра — по самым быстрым
  blaze: heroBehavior(), // ближний огонь по площади (радиус и взрыв — в данных)
  frosty: heroBehavior({ target: unslowedFirst, onHit: frostHit }),
  // премиум-герои беты: данные — `data/beta/heroes.json`
  radiation: heroBehavior({ target: densestCrowd }), // радиоактивный снаряд в густую толпу
  cryo: heroBehavior({ target: unslowedFirst, onHit: frostHit }), // ледяная граната: заморозка группы
  laser: heroBehavior(), // луч по группе
  plague: heroBehavior({ target: densestCrowd }), // чумной снаряд в густую толпу
};
